import type { Module } from 'vuex';
import { Agent } from '@/types/Agent';
import {RootState} from "@/store";

export interface AgentState {
  agents: Agent[];
  selectedIdAgent: number | null;
}

const agentModule: Module<AgentState, RootState> = {
  namespaced: true,

  state: () => ({
    agents: [],
    selectedIdAgent: null,
  }),

  getters: {
    selectedAgent(state): Agent | null {
      if (state.selectedIdAgent !== null) {
        return state.agents[state.selectedIdAgent] ?? null;
      }
      return null;
    },
    getSelectedAgentCode(state): string {
      const idAgent = state.selectedIdAgent;
      if (idAgent === null) return 'null';

      const agent = state.agents.find(agent => agent.idAgent === idAgent);
      return agent?.code ?? 'null';
    },
  },

  mutations: {
    setAgents(state, payload) {
      state.agents = Object.values(payload.agents).map(Agent.fromRaw);
    },

    setAgentCreate(
      state: AgentState,
      payload: {
        idAgent: number;
        code: string;
        agentSettings: { settingKey: string; defaultValue: string; idAgentSetting: number | null }[];
      }
    ) {
      const raw = {
        idAgent: payload.idAgent,
        code: payload.code,
        locked: false,
        agentSettings: {},
      };

      const agent = Agent.fromRaw(raw);
      agent.applySettings(payload.agentSettings);

      state.agents.push(agent);
    },

    setAgentUpdate(
      state: AgentState,
      payload: {
        idAgent: number;
        code: string;
        agentSettings: { idAgentSetting: number | null; settingKey: string; defaultValue: string }[];
      }
    ) {
      const agent = state.agents.find(agent => agent.idAgent === payload.idAgent);
      if (!agent) return;

      agent.code = payload.code;
      agent.applySettings(payload.agentSettings);
    },

    setAgentDelete(state: AgentState, payload: { idAgent: number }) {
      if (
        state.selectedIdAgent !== null &&
        state.agents[state.selectedIdAgent]?.idAgent === payload.idAgent
      ) {
        state.selectedIdAgent = null;
      }

      state.agents = state.agents.filter(agent => agent.idAgent !== payload.idAgent);
    },

    setAgentLock(state: AgentState, payload: { idAgent: number }) {
      const agent = state.agents.find(agent => agent.idAgent === payload.idAgent);
      if (agent) {
        agent.locked = true;
      }
    },

    setAgentUnlock(state: AgentState, payload: { idAgent: number }) {
      const agent = state.agents.find(agent => agent.idAgent === payload.idAgent);
      if (agent) {
        agent.locked = false;
      }
    },

    selectAgent(state: AgentState, idAgent: number | null) {
      state.selectedIdAgent = idAgent;
    },
  },
  actions: {
    wsSetAgents(context, payload: { agents: any }) {
      context.commit('setAgents', payload);
    },
    wsAgentCreate(context, payload: {
      idAgent: number;
      code: string;
      agentSettings: { settingKey: string; defaultValue: string; idAgentSetting: number | null }[];
    }) {
      context.commit('setAgentCreate', payload);
    },
    wsAgentUpdate(context, payload: {
      idAgent: number;
      code: string;
      agentSettings: { idAgentSetting: number | null; settingKey: string; defaultValue: string }[];
    }) {
      context.commit('setAgentUpdate', payload);
    },
    wsAgentDelete(context, payload: { idAgent: number }) {
      context.commit('setAgentDelete', payload);
    },
    wsAgentLock(context, payload: { idAgent: number }) {
      context.commit('setAgentLock', payload);
    },
    wsAgentUnlock(context, payload: { idAgent: number }) {
      context.commit('setAgentUnlock', payload);
    },
    selectAgent(context, payload: number | null) {
      context.commit('selectAgent', payload);
    },
  },
};

export default agentModule;
